import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Popover,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import Button from "../../../ui/elements/buttons/Button";
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';


interface Range {
  start?: number;
  end?: number;
  over?: number;
  under_2hrs?: string | number;
  over_2hrs?: string | number;
}

interface MatrixTableProps {
  price: {
    ranges: Range[];
  };
  // onRangesUpdate: (updatedRanges: Range[]) => void;
  onEditClick: (event: React.MouseEvent<HTMLElement>, index: number) => void;
  setUpdatedRanges: (updatedRanges: Range[]) => void; // Corrected type}
  matrixInitialValues: any[];
}

const MatrixTable: React.FC<MatrixTableProps> = ({ price,setUpdatedRanges, matrixInitialValues }) => {
  const [ranges, setRanges] = useState(price.ranges || matrixInitialValues);
  const [editingRange, setEditingRange] = useState<{ index: number; start: number; end: number } | null>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);


  const handleEditClick = (event: React.MouseEvent<HTMLElement>, index: number) => {
    const range = ranges[index];
    setEditingRange({
      index,
      start: range.start ?? 0,
      end: range.end ?? 0,
    });
    setAnchorEl(event.currentTarget);
  };

  const handleInputChange = (field: "start" | "end", value: string) => {
    if (editingRange) {
      setEditingRange({
        ...editingRange,
        [field]: parseInt(value, 10) || 0,
      });
    }
  };

  const handleSave = () => {
    if (editingRange) {
      const updatedRanges = [...ranges];
      updatedRanges[editingRange.index] = {
        ...updatedRanges[editingRange.index],
        start: editingRange.start,
        end: editingRange.end,
      };

      // Update the next range's start value
      if (editingRange.index < updatedRanges.length - 1) {
        updatedRanges[editingRange.index + 1].start = editingRange.end + 1;
      }

      setRanges(updatedRanges);// [{ "start": 1, "end": 9, "under_2hrs": 19, "over_2hrs": 19 },{ "start": 10, "end": 49, "under_2hrs": 19, "over_2hrs": 15 },{ "start": 50, "end": 99, "under_2hrs": 18, "over_2hrs": 10 },{ "over": 100, "under_2hrs": 16, "over_2hrs": 6 }]
   
      setUpdatedRanges(updatedRanges);
      setEditingRange(null);
    }
  };

  const handleClose = () => {
    setEditingRange(null);
    setAnchorEl(null);
  };

  const handleValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    rowIndex: number, 
    rangeIndex: number, 
    key: string
  ) => {
    const value = event.target.value;
    if (/^\d*$/.test(value)) { // Allow only numeric values
      const updatedRanges = [...ranges];
      updatedRanges[rangeIndex] = {
        ...updatedRanges[rangeIndex],
        [key]: value,
      };
      setRanges(updatedRanges);
      setUpdatedRanges(updatedRanges);
    }
  };
 
  
  

  return (
    <>
      <TableContainer>
        <Table
          sx={{
            "& .MuiTableRow-root": {
              height: "36px",
            },
            "& .MuiTableCell-root": {
              height: "36px",
              padding: "6px",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  color: "#000",
                  minWidth: "150px",
                  border: "1px solid #c4c4c4",
                }}
              >
                Price per Player
              </TableCell>
              {ranges?.map((range, index) => {
                const header =
                range.start && range.end
                  ? `${range.start} - ${range.end} Players`
                  : range.start
                  ? `${range.start}+ Players`
                  : range.end
                  ? `${range.end} Players`
                  : '';
                return (
                  <TableCell
                    key={index}
                    sx={{
                      color: "#000",
                      minWidth: "150px",
                      border: "1px solid #c4c4c4",
                      textAlign: "left",
                    }}
                  >
                    {header}
                    {index !== ranges.length - 1 && (
                      <IconButton
                      onClick={(e) => handleEditClick(e, index)}
                        sx={{
                          paddingLeft: "40px",
                          paddingRight: "0px",
                          ":hover": {
                            backgroundColor: "#fff",
                            cursor: "default",
                          },
                        }}
                      >
                        <EditIcon
                          sx={{
                            color: "#000",
                            fontSize: "18px",
                          }}
                        />
                      </IconButton>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {["under_2hrs", "over_2hrs"]?.map((key, rowIndex) => (
              <TableRow key={key}>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    border: "1px solid #c4c4c4",
                  }}
                >
                  {key === "under_2hrs"
                    ? "Stream under 2 hrs"
                    : "Stream over 2 hrs"}
                </TableCell>
                {ranges?.map((range, index) => {
                  const value = range[key as keyof Range] || "-";

                  return (
                    <TableCell
                      key={index}
                      sx={{
                        border: "1px solid #c4c4c4",
                        textAlign: "right",
                        color: "#000",
                        paddingRight: rowIndex === 1 ? "10px" : "6px",
                        minWidth: "150px",
                      }}
                    >
                      <TextField
                        variant="standard"
                        value={range[key as keyof Range] ?? ""}
                        onChange={(e) =>
                          handleValueChange(
                            e as React.ChangeEvent<HTMLInputElement>,
                            rowIndex,
                            index,
                            key
                          )
                        }
                        InputProps={{
                          disableUnderline: true, // Ensures no underline
                        }}
                        sx={{
                          "& .MuiInputBase-root": {
                            border: "none", // Removes all borders
                          },
                          "& .MuiInputBase-input": {
                            border: "none", // Ensures the input itself has no border
                            padding: "0",   // Adjust padding if needed
                          },
                          "& .MuiInput-root:before, & .MuiInput-root:after": {
                            borderBottom: "none !important", // Completely removes the bottom border
                          },
                        }}
                        size="small"
                        fullWidth
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {editingRange && (
       <Popover
       open={Boolean(anchorEl)}
       anchorEl={anchorEl}
       onClose={handleClose}
       anchorOrigin={{
         vertical: 'bottom',
         horizontal: 'center',
       }}
       transformOrigin={{
         vertical: 'top',
         horizontal: 'center',
       }}
       PaperProps={{ sx: { p: 2, width: 200 } }} // Adjust width as needed
     >
     
       <Box
         sx={{
           display: "flex",
           flexDirection: "column",
           gap: 2,
         }}
       >
         <TextField
           label="Lower Limit"
           type="number"
           value={editingRange.start}
           disabled
           size="small"
           onChange={(e) => handleInputChange("start", e.target.value)}
           InputProps={{
             startAdornment: (
               <InputAdornment position="start">
                 <MenuOutlinedIcon sx={{ fontSize: "16px" }} />
               </InputAdornment>
             ),
           }}
         />
         <TextField
           label="Upper Limit"
           type="number"
           value={editingRange.end}
           size="small"
           InputLabelProps={{
             shrink: true,
           }}
           sx={{ "& .MuiInputLabel-root": { color: "black" } }}
           onChange={(e) => handleInputChange("end", e.target.value)}
           InputProps={{
             startAdornment: (
               <InputAdornment position="start">
                 <MenuOutlinedIcon sx={{ fontSize: "16px" }} />
               </InputAdornment>
             ),
           }}
         />
         <Box justifyContent={"center"} alignItems={'center'} display="flex">
         <Button
           onClick={handleSave}
           sx={{
             background: "rgba(246, 51, 63, 1)",
             color: "white",
             fontWeight: 600,
             mt: 1,
           }}
         >
           Done
         </Button>
         </Box>
       </Box>
     </Popover>
      )}
    </>
  );
};

export default MatrixTable;
